import {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
  useImperativeHandle,
  forwardRef,
  ForwardRefRenderFunction,
  KeyboardEvent
} from 'react'
import { useIntl } from 'react-intl'
import { debounce, TextFieldProps } from '@mui/material'
import { SearchField, SearchFieldProps } from '@carto/meridian-ds/components'

const DEBOUNCE_TIME = 300

interface FilterInputProps {
  initialValue?: string
  placeholderId: string
  placeHolderValues?: Record<string, string | number | null | undefined>
  onChange: (value: string) => void
  fullWidth?: boolean
  disabled?: boolean
  onClick?: () => void
  onFocus?: () => void
  onBlur?: () => void
  onClear?: () => void
  onEnterPress?: (value: string) => void
  onKeyDown?: (event: KeyboardEvent) => void
  size?: TextFieldProps['size']
  testId?: string
  loading?: boolean
  minimized?: boolean
  variant?: SearchFieldProps['variant']
  debounceTime?: number
}

export interface FilterInputRefProps {
  clear: () => void
  setValue: (value: string) => void
  getValue: () => string
}

const FilterInput: ForwardRefRenderFunction<FilterInputRefProps, FilterInputProps> = (
  {
    initialValue,
    placeholderId,
    placeHolderValues,
    onChange,
    onClick,
    onFocus,
    onBlur,
    onClear,
    onEnterPress,
    onKeyDown,
    fullWidth = false,
    disabled = false,
    testId,
    size = 'small',
    variant = 'filled',
    loading = false,
    minimized = false,
    debounceTime = DEBOUNCE_TIME
  },
  forwardedRef
) => {
  const intl = useIntl()
  const [search, setSearch] = useState<string>(initialValue || '')
  const updateFilter = useMemo(() => debounce((search: string) => setSearch(search), debounceTime), [debounceTime])

  const handleChange = useCallback(
    (event: ChangeEvent<unknown>) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const search = (event.target as any).value
      setSearch(search)
      updateFilter(search)
    },
    [setSearch, updateFilter]
  )

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Enter') {
        onEnterPress?.(search)
      } else {
        onKeyDown?.(event)
      }
    },
    [onEnterPress, onKeyDown, search]
  )

  const clear = useCallback(() => {
    setSearch('')
    onClear?.()
    onChange('')
  }, [onChange, onClear])

  const setValue = useCallback(
    (value) => {
      setSearch(value)
    },
    [setSearch]
  )

  const getValue = useCallback(() => {
    return search
  }, [search])

  useEffect(() => {
    if (onChange && search !== initialValue) {
      onChange(search)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search])

  useImperativeHandle(forwardedRef, () => ({
    clear,
    setValue,
    getValue
  }))

  return (
    <SearchField
      fullWidth={fullWidth}
      placeholder={intl.formatMessage({ id: placeholderId }, placeHolderValues)}
      value={search}
      disabled={disabled}
      loading={loading}
      minimized={minimized}
      onClick={onClick}
      onFocus={onFocus}
      onBlur={onBlur}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      size={size}
      variant={variant}
      InputProps={{
        'data-testid': testId
      }}
    />
  )
}

FilterInput.displayName = 'FilterInput'

export default forwardRef(FilterInput)
